<template>
  <div
    class="ic-showcase"
    :class="{ 'is-live': started, 'is-fallback': fallback }"
    @pointerenter="onPointer($event, true)"
    @pointerleave="onPointer($event, false)"
    @focusin="hold(true)"
    @focusout="hold(false)"
  >
    <!-- Fallback only: the hero photo, when there is no listing to show
         (the read failed or nothing qualifies). While the listings load the
         box shows a soft placeholder instead. -->
    <picture v-if="fallback">
      <source
        type="image/webp"
        srcset="
          /img-opt/hero-home-640.webp 640w,
          /img-opt/hero-home-960.webp 960w,
          /img-opt/hero-home-1280.webp 1280w,
          /img-opt/hero-home-1600.webp 1600w,
          /img-opt/hero-home-1920.webp 1920w"
        sizes="100vw"
      />
      <img
        class="ic-showcase__img"
        src="/img-opt/hero-home-1280.jpg"
        alt=""
        width="3084"
        height="1341"
        decoding="async"
      />
    </picture>

    <!-- Listings: all stacked, only the active one opaque. Only the active
         slide and its neighbours are mounted, so the next one's images are
         already loading while the current one is on screen. -->
    <template v-for="(slide, i) in slides" :key="slide.id">
      <div
        v-if="isNear(i)"
        class="ic-showcase__slide"
        :class="{ 'is-active': started && i === index }"
        :aria-hidden="started && i === index ? 'false' : 'true'"
        data-testid="showcase-slide"
      >
        <img
          class="ic-showcase__img"
          :src="slide.photo.src"
          :srcset="slide.photo.srcset || null"
          :sizes="slide.photo.srcset ? imgSizes : null"
          :alt="`${slide.typology}, com'è oggi`"
          decoding="async"
          @load="onPhotoLoad(i)"
        />
        <img
          class="ic-showcase__img ic-showcase__render"
          :class="{ 'is-shown': i === index && imagined }"
          :src="slide.render.src"
          :srcset="slide.render.srcset || null"
          :sizes="slide.render.srcset ? imgSizes : null"
          :alt="`${slide.typology}, come potrebbe diventare`"
          decoding="async"
        />
      </div>
    </template>

    <template v-if="current">
      <div class="ic-showcase__switch" role="group" aria-label="Mostra la casa">
        <button
          type="button"
          class="ic-showcase__seg"
          :class="{ 'is-on': !imagined }"
          :aria-pressed="!imagined ? 'true' : 'false'"
          data-testid="showcase-today"
          @click="choose(false)"
        >Com'è oggi</button>
        <button
          type="button"
          class="ic-showcase__seg"
          :class="{ 'is-on': imagined }"
          :aria-pressed="imagined ? 'true' : 'false'"
          data-testid="showcase-imagined"
          @click="choose(true)"
        >Immaginata</button>
      </div>

      <router-link :to="`/annunci/${current.id}`" class="ic-showcase__caption" data-testid="showcase-link">
        <transition name="ic-showcase-fade" mode="out-in">
          <span :key="current.id" class="ic-showcase__text">
            <span class="ic-showcase__title">{{ current.typology }}<template v-if="current.place"> · {{ current.place }}</template></span>
            <span class="ic-showcase__meta">{{ metaLine }}</span>
          </span>
        </transition>
        <span class="ic-showcase__go" aria-hidden="true"><ArrowUpRight :size="20" /></span>
      </router-link>

      <template v-if="slides.length > 1">
        <button
          type="button"
          class="ic-showcase__arrow ic-showcase__arrow--prev"
          aria-label="Annuncio precedente"
          data-testid="showcase-prev"
          @click="go(-1)"
        ><ChevronLeft :size="18" aria-hidden="true" /></button>
        <button
          type="button"
          class="ic-showcase__arrow ic-showcase__arrow--next"
          aria-label="Annuncio successivo"
          data-testid="showcase-next"
          @click="go(1)"
        ><ChevronRight :size="18" aria-hidden="true" /></button>
      </template>

      <div v-if="slides.length > 1" class="ic-showcase__dots" aria-hidden="true">
        <span
          v-for="(slide, i) in slides"
          :key="slide.id"
          class="ic-showcase__dot"
          :class="{ 'is-on': i === index }"
        ></span>
      </div>
    </template>
  </div>
</template>

<script>
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-vue-next';

// Timeline of one listing: "Com'è oggi" for REVEAL_MS, then it melts into
// "Immaginata" and holds until SLIDE_MS, then the next listing fades in.
export const REVEAL_MS = 2600;
export const SLIDE_MS = 7000;

export default {
  name: 'HomeShowcase',
  components: { ArrowUpRight, ChevronLeft, ChevronRight },
  props: {
    // From pickShowcaseSlides(): { id, typology, place, price, works, photo, render }.
    slides: { type: Array, default: () => [] },
    // No listing to show (read failed or none qualifies): show the hero photo.
    fallback: { type: Boolean, default: false },
  },
  data() {
    return {
      index: 0,
      imagined: false,
      // False until the first listing's photograph is in.
      started: false,
      loaded: {},
      held: false,
      // The visitor picked a side: the auto-play leaves this listing alone.
      pinned: false,
      timers: [],
      imgSizes: '(min-width: 1024px) 50vw, 100vw',
    };
  },
  computed: {
    current() {
      return this.started ? this.slides[this.index] || null : null;
    },
    metaLine() {
      const c = this.current;
      return c ? [c.price, c.works].filter(Boolean).join(' · ') : '';
    },
    reducedMotion() {
      return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
  },
  watch: {
    slides() {
      this.reset();
      this.maybeStart();
      this.$nextTick(this.checkFirstPhoto);
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibility);
  },
  beforeUnmount() {
    this.clearTimers();
    document.removeEventListener('visibilitychange', this.onVisibility);
  },
  methods: {
    isNear(i) {
      const n = this.slides.length;
      if (!n) return false;
      return i === this.index || i === (this.index + 1) % n || i === (this.index - 1 + n) % n;
    },
    later(fn, ms) {
      this.timers.push(setTimeout(fn, ms));
    },
    clearTimers() {
      this.timers.forEach(clearTimeout);
      this.timers = [];
    },
    reset() {
      this.clearTimers();
      this.index = 0;
      this.imagined = false;
      this.started = false;
      this.pinned = false;
      this.loaded = {};
    },
    // A photo already in the cache can finish before the load listener sees
    // it: read its state directly instead of waiting for an event.
    checkFirstPhoto() {
      const img = this.$el && this.$el.querySelector('[data-testid="showcase-slide"] img');
      if (img && img.complete && img.naturalWidth > 0) this.onPhotoLoad(0);
    },
    onPhotoLoad(i) {
      this.loaded = { ...this.loaded, [i]: true };
      this.maybeStart();
    },
    // The first listing fades in once its photograph is actually in (never a
    // fade into an empty box).
    maybeStart() {
      if (this.started || !this.slides.length || !this.loaded[0]) return;
      this.started = true;
      this.play();
    },
    // Schedule the reveal and the move to the next listing.
    play() {
      this.clearTimers();
      if (this.held || this.pinned || this.reducedMotion) return;
      if (!this.imagined) this.later(() => { this.imagined = true; }, REVEAL_MS);
      if (this.slides.length > 1) {
        this.later(() => this.next(), this.imagined ? SLIDE_MS - REVEAL_MS : SLIDE_MS);
      }
    },
    next() {
      this.go(1);
    },
    // Step to the previous (-1) or next (+1) listing; the auto-play picks up
    // from there (or waits, while the pointer is still over the showcase).
    go(delta) {
      const n = this.slides.length;
      this.index = (this.index + delta + n) % n;
      this.imagined = false;
      this.pinned = false;
      this.play();
    },
    choose(imagined) {
      this.imagined = imagined;
      this.pinned = true;
      this.clearTimers();
    },
    hold(on) {
      this.held = on;
      if (!this.started) return;
      if (on) {
        this.clearTimers();
      } else if (this.pinned) {
        // Leaving a listing the visitor toggled: give it a moment, then move on.
        this.pinned = false;
        this.clearTimers();
        if (!this.reducedMotion && this.slides.length > 1) this.later(() => this.next(), REVEAL_MS);
      } else {
        this.play();
      }
    },
    // Mouse hover pauses; a finger "entering" on tap must not pin it forever.
    onPointer(event, on) {
      if (event.pointerType === 'mouse') this.hold(on);
    },
    onVisibility() {
      if (document.hidden) this.clearTimers();
      else if (this.started) this.play();
    },
  },
};
</script>
